import { acceptsIdempotencyKey, ApiError, createClient, createIdempotencyKey, isMutation, transportProblem, type ApiClient, type ClientEvents, type Problem, type RequestMeta, type RequestOptions } from "@baton/api";
import { onlineManager } from "@tanstack/query-core";
import type { EventApplier, OwnWrites } from "./events/reducer/reducer.ts";
import { createOfflineMachine, type OfflineMachine } from "./offline/machine.ts";
import { refreshActive, refreshAfterRecovery } from "./offline/recovery.ts";
import { createWriteQueue, type WriteQueue, type DroppedWrite } from "./offline/queue.ts";
import { createPermissionMirror, type PermissionMirror } from "./permissions/mirror.ts";
import type { Platform } from "./platform.ts";
import { createRateLimits, type RateLimits } from "./problems/rateLimit.ts";
import { createQueryClient, DEFAULT_GC_TIME, type BatonQueryClient } from "./query/client.ts";
import { createTypedStorage, type TypedStorage } from "./storage/local.ts";
import { createDrafts, type Drafts } from "./storage/drafts.ts";
import { createClockOffset, type ClockOffset } from "./time/offset.ts";
import { createEntityQueue, type EntityQueue } from "./mutations/serialise.ts";
import { createPendingStore, type PendingStore } from "./mutations/optimistic.ts";
import { createReconciler, type Reconciler } from "./mutations/reconcile.ts";

/**
 * The one place the runtime's parts are wired: the transport's hooks feed the clock offset,
 * the offline machine, the echo-suppression set and the permission mirror; the query client,
 * the rate limits, the write queue, the storage wrapper, the drafts, the entity queue, the
 * pending marks and the reconciler are created once and shared by every screen. The web
 * application calls `createRuntime` in `main.tsx` with the browser platform.
 */
export interface Runtime {
  /** The transport as sent: a 401 is an answer. Public routes and the re-authentication dialog use it. */
  readonly client: ApiClient;
  /** The transport every signed-in surface uses: a 401 parks the request behind the dialog (FR-015-013). */
  readonly signedIn: ApiClient;
  readonly queryClient: BatonQueryClient;
  readonly clock: ClockOffset;
  readonly offline: OfflineMachine;
  readonly writeQueue: WriteQueue;
  readonly permissions: PermissionMirror;
  readonly rateLimits: RateLimits;
  readonly storage: TypedStorage;
  readonly drafts: Drafts;
  readonly entityQueue: EntityQueue;
  readonly pending: PendingStore;
  readonly reconciler: Reconciler;
  /** The host's platform seams, for the upload machine's sender and hash (§ File uploads). */
  readonly platform: Platform;
  /**
   * Adopt the applier for one workspace's stream, which `create` builds over this runtime's own
   * writes and clock: the request ids this client issues reach its echo guard, and a sign-out clears
   * it. The shell's stream engine builds it, so the reducer is that lazy chunk's and not the first
   * load's (2026-10-14).
   */
  adoptApplier(create: (own: OwnWrites, now: (() => number) | undefined) => EventApplier): EventApplier;
  /** Wipe everything a session owned: the cache, the queue, the drafts, the mirror, the conflicts. */
  signOut(options?: { everywhere?: boolean }): void;
  readonly reauth: ReauthState;
}

/** A request the transport parked behind the dialog, waiting to be replayed as it was sent. */
export interface ParkedRequest {
  readonly method: string;
  readonly path: string;
}

/**
 * FR-015-013, `contracts/client-api.md` § Session lifecycle: a 401 from a signed-in surface
 * parks the failing request, opens one dialog over the current screen, and replays every
 * parked request with its original idempotency key once sign-in succeeds. Concurrent 401s
 * park behind the same dialog. A dismissal rejects every parked request and clears the
 * drafts (FR-015-016).
 */
export interface ReauthState {
  readonly required: boolean;
  readonly problem: Problem | null;
  readonly parked: readonly ParkedRequest[];
  readonly subscribe: (listener: () => void) => () => void;
  /** Sign-in succeeded inside the dialog: the parked requests replay. */
  resolved(): void;
  /** The dialog was dismissed without a sign-in: the parked requests fail and drafts are cleared. */
  dismissed(): void;
}

export interface RuntimeOptions {
  readonly platform: Platform;
  readonly baseUrl?: string;
  readonly onDroppedWrite: (dropped: DroppedWrite) => void;
  /** The shell's notice for a 403 `csrf`: one silent reload. */
  readonly reload: () => void;
  /** The words an action still refused `overloaded` after its retries fails with (the client's
   * `overloadedWords`); a runtime without them keeps the server's. */
  readonly overloadedWords?: () => string;
  /** The words a request that ran out of its own time fails with (the client's `timeoutWords`, 2026-10-13); a runtime without them keeps the client's. */
  readonly timeoutWords?: () => string;
  readonly now?: () => number;
}

interface Waiter {
  readonly request: ParkedRequest;
  readonly resolve: () => void;
  readonly reject: (error: unknown) => void;
}

interface ReauthInternal extends ReauthState {
  /** Park a request behind the dialog; resolves when sign-in succeeds, rejects on dismissal. */
  park(request: ParkedRequest, problem: Problem): Promise<void>;
}

function createReauthState(drafts: Drafts): ReauthInternal {
  let required = false;
  let problem: Problem | null = null;
  let waiters: Waiter[] = [];
  const listeners = new Set<() => void>();
  const notify = () => {
    for (const listener of listeners) listener();
  };
  const settle = (outcome: "resolved" | "dismissed") => {
    const batch = waiters;
    waiters = [];
    required = false;
    const failure = problem;
    problem = null;
    notify();
    for (const waiter of batch) {
      if (outcome === "resolved") waiter.resolve();
      else waiter.reject(new ApiError(failure ?? transportProblem(401, "")));
    }
  };
  return {
    get required() {
      return required;
    },
    get problem() {
      return problem;
    },
    get parked() {
      return waiters.map((w) => w.request);
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => void listeners.delete(listener);
    },
    resolved() {
      settle("resolved");
    },
    dismissed() {
      drafts.clearAll();
      settle("dismissed");
    },
    park(request, p) {
      if (!required) {
        required = true;
        problem = p;
      }
      const promise = new Promise<void>((resolve, reject) => {
        waiters.push({ request, resolve, reject });
      });
      notify();
      return promise;
    },
  };
}

const REAUTH_TYPES: ReadonlySet<string> = new Set(["unauthenticated", "token_expired", "token_revoked"]);

/**
 * The client every signed-in surface uses: a mutation's idempotency key is minted before
 * the first send so a replay carries the same one, and a 401 parks the request behind the
 * dialog and replays it verbatim after sign-in. The plain `client` never parks, which is
 * what a public route and the dialog's own sign-in use.
 */
function withReauth(client: ApiClient, reauth: ReauthInternal): ApiClient {
  return {
    config: client.config,
    async request(options) {
      const keyed: RequestOptions = isMutation(options.method ?? "GET") && acceptsIdempotencyKey(options.path) && options.idempotencyKey === undefined ? { ...options, idempotencyKey: createIdempotencyKey() } : options;
      try {
        return await client.request(keyed);
      } catch (error) {
        if (!(error instanceof ApiError) || !REAUTH_TYPES.has(error.problem.type)) throw error;
        await reauth.park({ method: keyed.method ?? "GET", path: keyed.path }, error.problem);
        return client.request(keyed);
      }
    },
  };
}

export function createRuntime(options: RuntimeOptions): Runtime {
  const { platform } = options;
  const clock = createClockOffset(options.now);
  const rateLimits = createRateLimits(options.now);
  const queryClient = createQueryClient({ rateLimits });
  const permissions = createPermissionMirror();
  const storage = createTypedStorage(platform.storage);
  const drafts = createDrafts(platform.storage, storage, options.now);
  const entityQueue = createEntityQueue();
  const pending = createPendingStore();
  const reconciler = createReconciler(entityQueue);
  const reauth = createReauthState(drafts);
  const appliers = new Set<EventApplier>();
  let paused = false;

  const events: ClientEvents = {
    onRequest(meta) {
      for (const applier of appliers) applier.issued(meta.requestId);
    },
    onResponse(_meta, response) {
      clock.observe(response.headers);
      offline.transportSucceeded();
    },
    onNetworkFailure() {
      offline.transportFailed();
    },
    onProblem(meta, problem, headers) {
      routeProblem(meta, problem, headers);
    },
  };

  const client = createClient({
    ...(options.baseUrl === undefined ? {} : { baseUrl: options.baseUrl }),
    ...(options.overloadedWords === undefined ? {} : { overloadedWords: options.overloadedWords }),
    ...(options.timeoutWords === undefined ? {} : { timeoutWords: options.timeoutWords }),
    events,
  });

  const offline = createOfflineMachine({
    online: platform.online,
    focus: platform.focus,
    ready: async (timeoutMs) => {
      const controller = new AbortController();
      const timer = setTimeout(() => {
        controller.abort();
      }, timeoutMs);
      try {
        await client.request({ path: "/readyz", retry: false, signal: controller.signal });
        return true;
      } catch {
        return false;
      } finally {
        clearTimeout(timer);
      }
    },
    pauseGc(next) {
      paused = next;
      queryClient.setDefaultOptions({
        ...queryClient.getDefaultOptions(),
        queries: { ...queryClient.getDefaultOptions().queries, gcTime: paused ? Infinity : DEFAULT_GC_TIME },
      });
    },
    onReconnect(from) {
      void writeQueue.drain().then(async () => {
        await refreshAfterRecovery(queryClient, from);
        if (offline.state !== "offline") onlineManager.setOnline(true);
      });
    },
  });

  const writeQueue = createWriteQueue({ onDropped: options.onDroppedWrite, invalidateActive: () => refreshActive(queryClient), ...(options.now ? { clock: options.now } : {}) });

  // § Offline detection and the write queue: one mechanism owns the reconnect refetch. The
  // cache's online state follows the offline machine rather than the browser's `online` event,
  // so no read is sent before the probe confirms the connection; it goes offline with the
  // machine and comes back only after the drain above, so each query that was paused or marked is
  // read exactly once, after the queued writes have landed (`query/client.ts` turns the cache's own
  // reconnect refetch off). Which queries are marked, and which are re-read rather than only marked,
  // is `offline/recovery.ts`: a paged list keeps the pages the reader loaded.
  onlineManager.setEventListener((setOnline) => {
    setOnline(offline.state !== "offline");
    return offline.subscribe((state) => {
      if (state === "offline") setOnline(false);
    });
  });

  function routeProblem(meta: RequestMeta, problem: Problem, headers: Headers): void {
    // A 401 is routed by `withReauth` on the signed-in client, because whether it opens the
    // dialog depends on the surface that made the request, not on the response alone.
    switch (problem.type) {
      case "csrf":
        options.reload();
        break;
      case "forbidden": {
        const scope = scopeOf(meta.url);
        if (scope) permissions.recordRefusal(`${meta.method} ${scope.path}`, scope);
        break;
      }
      case "rate_limited": {
        const retryAfter = headers.get("Retry-After");
        const seconds = retryAfter === null ? 0 : Number(retryAfter);
        const family = familyOf(meta.url);
        if (family) rateLimits.pause(family, Number.isFinite(seconds) ? seconds * 1000 : 0);
        break;
      }
      default:
        break;
    }
  }

  return {
    client,
    signedIn: withReauth(client, reauth),
    queryClient,
    clock,
    offline,
    writeQueue,
    permissions,
    rateLimits,
    storage,
    drafts,
    entityQueue,
    pending,
    reconciler,
    platform,
    reauth,
    adoptApplier(create) {
      const applier = create({ observe: (entityId, version) => { entityQueue.observe(entityId, version); }, inFlight: (entityId) => entityQueue.pending(entityId) > 0 }, options.now);
      appliers.add(applier);
      return applier;
    },
    signOut({ everywhere = false } = {}) {
      queryClient.clear();
      drafts.clearAll();
      permissions.clear();
      reconciler.clear();
      entityQueue.clear();
      if (everywhere) writeQueue.clear();
      for (const applier of appliers) applier.clearSuppression();
    },
  };
}

/** The workspace and project a request URL addresses, for the permission mirror's key. */
export function scopeOf(url: string): { workspace: string; project?: string; path: string } | null {
  const match = /\/api\/v1\/workspaces\/([^/?#]+)((?:\/projects\/([^/?#]+))?[^?#]*)/.exec(url);
  if (!match) return null;
  const workspace = match[1] ?? "";
  const rest = match[2] ?? "";
  const project = match[3];
  const path = rest.replace(/\/[0-9a-f]{8}-[0-9a-f-]{27}\b/gi, "/{id}").replace(/\/[A-Z]+-\d+\b/g, "/{item}");
  return project === undefined ? { workspace, path } : { workspace, project, path };
}

/** The query family a 429 pauses: search for the search route, otherwise the workspace. */
export function familyOf(url: string): readonly unknown[] | null {
  const scope = scopeOf(url);
  if (!scope) return null;
  if (scope.path.startsWith("/search")) return ["ws", scope.workspace, "search"];
  return ["ws", scope.workspace];
}
